import { events } from "@constants/tracking";

import { getAppVersion } from "@helpers/app-version";
import { isOlxExamWithinDateRange } from "@helpers/exams";
import i18n from "@helpers/lib/i18n";
import { formatDate } from "@memorang/helpers/date";
import { getPlatform } from "@memorang/helpers/platform";
import type { ExamDetails } from "@memorang/types";
import { trackCustomEvent } from "analytics";
import { fetchExamDetails, fetchExamsList } from "api/commerce/exam-service";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
import type { Dispatch } from "react";
import type { SetStateAction } from "react";

export const getOlxPostRedeemDialogConfig = (
	beginStartDate: string,
	endStartDate: string,
	examName: string,
	bundleName: string,
) => {
	const now = Date.now();
	const beginTime = new Date(beginStartDate).getTime();
	const endTime = new Date(endStartDate).getTime();
	const locale = i18n.language;

	let title = i18n.t("redeem.olxDialog.unlockedTitle");
	let body = "";
	let confirmCta: string | undefined;
	let cancelCta = i18n.t("common.ok");

	const testInfo = `${i18n.t("redeem.olxDialog.testInfo", { examName, bundleName })}

---
`;

	if (now < beginTime) {
		const daysUntilStart = Math.ceil((beginTime - now) / (1000 * 60 * 60 * 24));
		body = `${testInfo}
${i18n.t("redeem.olxDialog.upcomingBody", { count: daysUntilStart })}`;
	} else if (isOlxExamWithinDateRange(beginStartDate, endStartDate)) {
		body = `${testInfo}
${i18n.t("redeem.olxDialog.activeBody", {
	date: formatDate(endStartDate, locale),
})}`;
		confirmCta = i18n.t("redeem.olxDialog.startNowCta");
		cancelCta = i18n.t("redeem.olxDialog.startLaterCta");
	} else if (now > endTime) {
		title = i18n.t("redeem.olxDialog.expiredTitle");
		body = `${testInfo}
${i18n.t("redeem.olxDialog.expiredBody", {
	date: formatDate(endStartDate, locale),
})}`;
	}

	return {
		title,
		body,
		confirmCta,
		cancelCta,
	};
};

export const getSupportDetails = (
	appName: string,
	username: string,
	email: string,
) => {
	const version = getAppVersion();
	const platform = getPlatform();
	const subject = `[${appName}] Access Code Issue`;
	const body = `Hey ${appName} support, my username is ${username} and email is ${email}. I'm using version ${version} of the ${appName} ${platform} app. I'm having some trouble with my access code. I've included it here: [copy paste your access code here]
  
Thank you!`;

	return { subject, body, version, platform };
};

export const switchExamContext = async (
	examId: string,
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>,
) => {
	try {
		const examDetailsLatest = await fetchExamDetails({ examId });

		if (examDetailsLatest) {
			// console.info("examDetailsLatest", examDetailsLatest);
			await handleSwitchExamContext(examDetailsLatest);
			return;
		}
		trackCustomEvent({
			eventName: events.settingExamContextFromExamDetailsFailed,
			examId,
			examDetailsResult: JSON.stringify(examDetailsLatest),
		});

		const exams = await fetchExamsList();
		const examDetails = exams.find(
			(examDetails) => examDetails.exam.id === examId,
		);
		if (examDetails) {
			await handleSwitchExamContext(examDetails);
		}
	} catch (error) {
		trackCustomEvent({
			eventName: events.postRedeemExamContextSwitchFailed,
			examId,
			error: JSON.stringify(error),
		});
	}
};

export const handleAccessCodeResult = async (
	result: ApplyAccessCodeResponse,
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>,
	setOlxDates: Dispatch<
		SetStateAction<{
			beginStartDate: string;
			endStartDate: string;
			tempAccessCodeResponse?: ApplyAccessCodeResponse;
		} | null>
	>,
) => {
	const examId =
		typeof result.data.examId === "string"
			? result.data.examId
			: result.productData?.[0]?.examId;
	const examName =
		typeof result.data.examName === "string"
			? result.data.examName
			: result.productData?.[0]?.examName;
	trackCustomEvent({
		eventName: events.promoCodeRedeemed,
		examId,
		examName,
	});
	if (examId) {
		await switchExamContext(examId, handleSwitchExamContext);
	}
	if (
		result.data.beginStartDate &&
		result.data.endStartDate &&
		!result.isSameEmail
	) {
		setOlxDates({
			beginStartDate: result.data.beginStartDate,
			endStartDate: result.data.endStartDate,
			tempAccessCodeResponse: result,
		});
		return { shouldHandleNext: false };
	}

	return { shouldHandleNext: true };
};
